import {
  CategorieVehicule,
  CategorieVehiculeBus,
  CategorieVehiculeLourd,
  ClientType,
  NouveauDossierConcessionnaireData,
  usageLourdParCategories,
  usageParticulierParCategories,
  UsageVehiculeParticulier,
  VehiculeType,
} from 'types/Concessionnaire';
import { M1_CONDITIONAL_QUESTION_NAMES } from 'views/dossiers/concessionnaire/steps/step2/step2.rules';

interface QuestionsSectionFieldsArgs {
  personnePhysique: boolean;
  categorieVehicule?:
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd;
  usageVehicule?: UsageVehiculeParticulier;
}

interface ShowM1ConditionalQuestionsArgs {
  personnePhysique: boolean;
  categorieVehicule?:
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd;
  usageVehicule?: UsageVehiculeParticulier;
}

interface UsageOptionsArgs {
  personnePhysique: boolean;
  isVehiculeLourd: boolean;
  categorieVehicule?:
    | CategorieVehicule
    | CategorieVehiculeBus
    | CategorieVehiculeLourd;
}

export function isPersonnePhysique(typeClient?: ClientType) {
  return typeClient === ClientType.PERSONNE_PHYSIQUE;
}

export function isVehiculeLourd(vehiculeType?: VehiculeType) {
  return !!vehiculeType && vehiculeType !== VehiculeType.LEGER;
}

export function isBus(vehiculeType?: VehiculeType) {
  return vehiculeType === VehiculeType.BUS;
}

export function getCommandeSectionFields(personnePhysique: boolean) {
  return personnePhysique
    ? ([
        'nbDevis',
        'dateEditionDevis',
        'datePrevisionnelleDevis',
        'devis',
      ] as (keyof NouveauDossierConcessionnaireData)[])
    : ([] as (keyof NouveauDossierConcessionnaireData)[]);
}

export function getQuestionsSectionFields({
  personnePhysique,
  categorieVehicule,
  usageVehicule,
}: QuestionsSectionFieldsArgs) {
  if (!personnePhysique) {
    return [] as (keyof NouveauDossierConcessionnaireData)[];
  }

  const fields: (keyof NouveauDossierConcessionnaireData)[] = [
    'categorieVehicule',
    'dateLivraison',
  ];

  if (categorieVehicule) {
    fields.push('usageVehiculeParticulier');
  }

  if (
    shouldShowM1ConditionalQuestions({
      personnePhysique,
      categorieVehicule,
      usageVehicule,
    })
  ) {
    fields.push(...M1_CONDITIONAL_QUESTION_NAMES);
  }

  return fields;
}

export function shouldShowM1ConditionalQuestions({
  personnePhysique,
  categorieVehicule,
  usageVehicule,
}: ShowM1ConditionalQuestionsArgs) {
  return (
    personnePhysique &&
    categorieVehicule === CategorieVehicule.M1 &&
    [
      UsageVehiculeParticulier.ACHAT,
      UsageVehiculeParticulier.LOCATION,
    ].includes(usageVehicule as UsageVehiculeParticulier)
  );
}

export function getUsageOptions({
  personnePhysique,
  isVehiculeLourd,
  categorieVehicule,
}: UsageOptionsArgs): string[] {
  if (!categorieVehicule) {
    return [];
  }

  if (isVehiculeLourd) {
    return (
      usageLourdParCategories[
        categorieVehicule as keyof typeof usageLourdParCategories
      ] ?? []
    );
  }

  if (personnePhysique) {
    return (
      usageParticulierParCategories[
        categorieVehicule as keyof typeof usageParticulierParCategories
      ] ?? []
    );
  }

  return [];
}

export function getVehiculeCategoryOptions(
  isVehiculeLourdValue: boolean,
  isBusValue: boolean,
) {
  if (!isVehiculeLourdValue) {
    return Object.values(CategorieVehicule);
  }

  return Object.values(
    isBusValue ? CategorieVehiculeBus : CategorieVehiculeLourd,
  );
}
